import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Label,
} from '@lognai/synapse-design-system-v2';

interface LinkContext {
  companyId: string;
  companyName: string | null;
  templateName: string;
  /** New model: the existing partner company being onboarded (editable prefill). */
  supplierCompanyId?: string;
  supplierCompanyName?: string | null;
  supplierRegistrationNumber?: string | null;
}

export default function SupplierRegistrationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [linkContext, setLinkContext] = useState<LinkContext | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);

  const [companyName, setCompanyName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `/api/vendor-onboarding/portal/registration-links/${encodeURIComponent(token)}`
        );
        const body = await res.json().catch(() => ({}));
        if (!active) return;
        if (res.ok && body.success) {
          const data = body.data as LinkContext;
          setLinkContext(data);
          // New model: prefill the (editable) company fields from the existing
          // partner company this link onboards.
          if (data.supplierCompanyName) setCompanyName(data.supplierCompanyName);
          if (data.supplierRegistrationNumber)
            setRegistrationNumber(data.supplierRegistrationNumber);
        } else {
          setLinkError(body.error || 'This registration link is invalid or has expired.');
        }
      } catch {
        if (active) setLinkError('Could not validate the registration link.');
      } finally {
        if (active) setValidating(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  if (!token) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
          <div className="text-4xl font-bold text-primary">LogNAI</div>
          <CardTitle className="text-xl font-normal">Invalid Registration Link</CardTitle>
          <CardDescription>
            This link is missing a token. Please use the full link provided to you.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (validating) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
          <div className="text-4xl font-bold text-primary">LogNAI</div>
          <CardTitle className="text-xl font-normal">Validating link…</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (linkError) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
          <div className="text-4xl font-bold text-primary">LogNAI</div>
          <CardTitle className="text-xl font-normal">Registration Unavailable</CardTitle>
          <CardDescription>{linkError}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/api/vendor-onboarding/portal/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, companyName, registrationNumber, fullName, email, password }),
      });
      const body = await res.json().catch(() => ({}));
      setPending(false);
      if (res.ok && body.success) {
        navigate('/login', {
          replace: true,
          state: { banner: 'Registration complete. Sign in to complete your questionnaire.' },
        });
      } else {
        setError(body.error || 'Registration failed. Please check your details and try again.');
      }
    } catch {
      setPending(false);
      setError('Registration failed. Please try again.');
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
        <div className="text-4xl font-bold text-primary">LogNAI</div>
        <CardTitle className="text-xl font-normal">Supplier Registration</CardTitle>
        <CardDescription>
          {linkContext?.companyName
            ? `Register as a supplier for ${linkContext.companyName}.`
            : 'Register as a supplier.'}{' '}
          You will then sign in to complete the {linkContext?.templateName ?? 'qualification questionnaire'}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Company legal name</Label>
            <Input
              id="company-name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company's legal name"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-number">Company registration number (optional)</Label>
            <Input
              id="reg-number"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder="Registration / CR number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="full-name">Your full name</Label>
            <Input
              id="full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Work email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              required
            />
          </div>
          {error && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Registering…' : 'Register'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
